import { FormControl, Icon } from "@mui/material";
import darkColors from "assets/theme-dark/base/colors";
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ESCURO, FONTE } from "assets/coresTema";

const { size } = typography;
const { borderRadius } = borders;

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <Icon sx={{ fontSize: "1.1rem !important" }}>arrow_drop_down</Icon>
        </components.DropdownIndicator>
    );
};

const ClearIndicator = (props) => {
    return (
        <components.ClearIndicator {...props}>
            <Icon sx={{ fontSize: "0.95rem !important" }}>close</Icon>
        </components.ClearIndicator>
    );
};

const MDSelect = ({
    theme,
    isDisabled = false,
    value,
    name,
    options,
    onChange,
    placeholder = "Selecione...",
    isMulti = false,
    creatable = false,
    size: selectSize = "medium",
    ...rest }) => {
    const [controller, dispatch] = useMaterialUIController();
    const { darkMode } = controller;

    const { transparent, inputBorderColor, white, grey } = darkMode ? darkColors : colors;
    
    const SelectComponent = creatable ? CreatableSelect : Select;

    const isSmall = selectSize === "small";
    // mesma altura dos outros campos (MDInput): 38px nos dois tamanhos
    const targetMinHeight = pxToRem(38);
    const targetHeight = isMulti ? 'auto' : targetMinHeight;

    const resolvedGetOptionValue = (option) => {
        if (!option) return "";
        let val;
        if (rest.getOptionValue) {
            val = rest.getOptionValue(option);
        } else {
            val = option.value !== undefined ? option.value : option.id;
        }
        return val === null || val === undefined ? "" : String(val);
    };

    return (
        <FormControl fullWidth>
            <SelectComponent
                {...rest}
                components={{ DropdownIndicator, ClearIndicator, ...rest.components }}
                getOptionValue={resolvedGetOptionValue}
                closeMenuOnSelect={true}
                name={name}
                value={value}
                options={options}
                onChange={onChange}
                isDisabled={isDisabled}
                placeholder={placeholder}
                isMulti={isMulti}
                noOptionsMessage={() => "Nenhuma opção"}
                loadingMessage={() => "Carregando..."}
                menuPortalTarget={document.body} // <-- aqui é o segredo
                styles={{
                    control: (styles, state) => {
                        // mesmas cores do MDInput (fundo transparente, borda e desabilitado do tema)
                        const disabledBg = darkMode ? ESCURO.painelEscuro : grey[200];
                        const disabledText = darkMode ? ESCURO.textoSecundario : grey[600];
                        const disabledBorder = darkMode ? ESCURO.divisoria : grey[300];
                        const enabledBorder = darkMode ? ESCURO.borda : rgba(inputBorderColor, 1);
                        const enabledBg = transparent.main;

                        return {
                            ...styles,
                            backgroundColor: state.isDisabled ? disabledBg : enabledBg,
                            color: state.isDisabled ? disabledText : (darkMode ? white.main : grey[800]),
                            borderColor: state.isDisabled ? disabledBorder : (state.isFocused ? (darkMode ? "#00b0ff !important" : "#1A73E8 !important") : enabledBorder),
                            boxShadow: state.isFocused ? (darkMode ? "0 0 0 1px #00b0ff" : "0 0 0 1px #1A73E8") : "none",
                            "&:hover": {
                                borderColor: state.isDisabled ? disabledBorder : (darkMode ? `${ESCURO.bordaForte} !important` : undefined)
                            },
                            opacity: 1,
                            pointerEvents: state.isDisabled ? "none" : "auto",
                            fontSize: FONTE.campo,
                            minHeight: targetMinHeight,
                            height: targetHeight,
                            borderRadius: borderRadius.md,
                            boxSizing: 'border-box',
                        };
                    },
                    valueContainer: (styles) => ({
                        ...styles,
                        flexWrap: isMulti ? 'wrap' : 'nowrap',
                        padding: `0 ${pxToRem(10)}`,
                        minHeight: pxToRem(36),
                        display: 'flex',
                        alignItems: 'center',
                    }),
                    singleValue: (styles) => ({
                        ...styles,
                        color: darkMode ? "#FFFFFF !important" : grey[800],
                        fontSize: FONTE.campo,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 8px)',
                        margin: 0,
                    }),
                    placeholder: (styles) => ({
                        ...styles,
                        color: darkMode ? `${ESCURO.textoFraco} !important` : grey[600],
                        fontSize: FONTE.campo,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'calc(100% - 8px)',
                        margin: 0,
                    }),
                    input: (styles) => ({
                        ...styles,
                        color: darkMode ? "#FFFFFF !important" : grey[800],
                        fontSize: FONTE.campo,
                        padding: 0,
                        margin: 0,
                    }),
                    multiValue: (styles) => ({
                        ...styles,
                        backgroundColor: darkMode ? ESCURO.hover : '#e0e0e0',
                        borderRadius: '4px',
                        margin: isSmall ? '1px 2px' : '2px 3px',
                    }),
                    multiValueLabel: (styles) => ({
                        ...styles,
                        color: darkMode ? '#FFFFFF !important' : '#333333',
                        fontSize: isSmall ? '10px' : '11px',
                        padding: isSmall ? '1px 4px' : '2px 6px',
                    }),
                    multiValueRemove: (styles) => ({
                        ...styles,
                        color: darkMode ? '#FFFFFF !important' : '#333333',
                        padding: isSmall ? '0 2px' : '2px',
                        '&:hover': {
                            backgroundColor: darkMode ? ESCURO.hover : '#d0d0d0',
                            color: '#ff4d4f',
                        },
                    }),
                    option: (styles, { isDisabled, isFocused, isSelected }) => {
                        return {
                            ...styles,
                            fontSize: size.xs,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            color: isSelected 
                                ? '#ffffff !important' 
                                : (darkMode ? '#FFFFFF !important' : grey[800]),
                            backgroundColor: isSelected 
                                ? '#1A73E8 !important' 
                                : isFocused 
                                ? (darkMode ? `${ESCURO.campo} !important` : '#e3f2fd !important') 
                                : (darkMode ? `${ESCURO.painelEscuro} !important` : '#ffffff !important'),
                            padding: `${pxToRem(8)} ${pxToRem(12)}`,
                            '&:active': {
                                backgroundColor: isSelected ? '#1A73E8 !important' : '#bbdefb !important'
                            }
                        };
                    },
                    indicatorsContainer: (styles) => ({
                        ...styles,
                        alignItems: 'center',
                    }),
                    indicatorSeparator: () => ({
                        display: 'none',
                    }),
                    dropdownIndicator: (styles) => ({
                        ...styles,
                        padding: isSmall ? pxToRem(2) : pxToRem(4),
                        color: darkMode ? "rgba(255, 255, 255, 0.6) !important" : "#7b809a !important",
                        '&:hover': {
                            color: darkMode ? "#ffffff !important" : "#344767 !important",
                        },
                    }),
                    clearIndicator: (styles) => ({
                        ...styles,
                        padding: isSmall ? pxToRem(2) : pxToRem(4),
                        color: darkMode ? "rgba(255, 255, 255, 0.6) !important" : "#7b809a !important",
                        '&:hover': {
                            color: darkMode ? "#ffffff !important" : "#344767 !important",
                        },
                    }),
                    menu: (styles) => ({
                        ...styles,
                        backgroundColor: darkMode ? `${ESCURO.painelEscuro} !important` : "#ffffff !important",
                        border: darkMode ? `1px solid ${ESCURO.borda}` : "1px solid #e0e0e0",
                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
                        zIndex: 99999,
                    }),
                    menuList: (styles) => ({
                        ...styles,
                        backgroundColor: darkMode ? `${ESCURO.painelEscuro} !important` : "#ffffff !important",
                        padding: 0,
                    }),
                    menuPortal: (base) => ({
                        ...base,
                        zIndex: 99999,
                    }),
                }}
            />
        </FormControl>

    )
};

MDSelect.propTypes = {
    isDisabled: PropTypes.bool,
    name: PropTypes.string.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    isMulti: PropTypes.bool
};

export default MDSelect
